'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Copy, ExternalLink, Globe, RefreshCw, ShieldAlert, Smartphone, Trash2, Undo2 } from 'lucide-react'
import { EntryDetailShell, type DetailBadge } from '../EntryDetail/EntryDetailShell'
import { detailColSpan } from '../EntryDetail/EntryDetailPanel'
import { Widget } from '../Widget'
import Turnstile, { type TurnstileHandle } from '../Turnstile'
import { ScreenshotLightbox, ImageTile, extractScreenshotImages } from '../ProcessedEntry/DomainEntry/imageComponents'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDate, compactDateNumeric } from '../ProcessedEntry/DomainEntry/processedTypes'
import { apiGet, apiPost } from './entriesApi'
import { siblingDetailHref } from './useDetailPath'
import { verifyRunLabel } from './jobTypes'
import { fmtAge } from './autoDomainChips'
import { ActionsWidget } from '../EntryDetail/ActionsWidget'
import { PipelineStepsWidget } from '../PipelineStepsWidget'
import { EntryTimelineWidget } from '../EntryDetail/EntryTimelineWidget'
import { InvestigationSidebar, type InvestigationAction, type InvestigationActionResult } from '../InvestigationSidebar'
import { ActionableEvidencePanel, deriveActionableEvidence } from './AdminSubmitEntryView'
import {
  BASIS_LABEL,
  BASIS_TITLE,
  BRAND_POSITION_LABEL,
  CHIP_TONE_CLASS,
  LEGACY_BASIS_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  VERDICT_LABEL,
  VERDICT_ORDER,
  type CandidateVerdict,
  VERDICT_TONE,
  VANTAGE_LABEL,
  VANTAGE_TITLE,
  VANTAGE_TONE,
  verdictOf,
  type AutoDomainAnalysisPass,
  type AutoDomainAnalysisRow,
  type AutoDomainCandidateDetail as DetailData,
  aliveForDays,
} from './autoDomainTypes'

// The evidence of record is the BEST pass, not the newest. Highest vantage
// first (5G -- the user's own machine, no proxy -- over the Azure worker
// through Webshare, which lures detect), then the pass's own verdict rank,
// then what the row actually holds; older wins a tie, so the first contact
// -- where a lure shows its real face -- is what the widgets show, and a
// later 403 or expired-session render only appears as "latest probe saw".
const VANTAGE_RANK: Record<string, number> = { dev_direct: 0, azure_proxy: 0, '5g': 1 }
const VERDICT_RANK: Record<string, number> = { dead: 0, unreachable: 1, blocked: 2, empty: 3, parked: 4, live_other: 5, brand_mention: 6, impersonation_other: 7, brand_lure: 8 }

function rowSubstance(row: AutoDomainAnalysisRow): number {
  const r = row.raw_result ?? {}
  if (row.analysis_layer === 'http') {
    return (r.ok && (Number(r.status) || 500) < 500 ? 1000 : 0) + Math.min(999, Number(r.text_length) || 0) + ((Number(r.form_count) || 0) > 0 ? 50 : 0)
  }
  if (row.analysis_layer === 'content') {
    const looked = Array.isArray(r.images_used) ? r.images_used.length * 10 : (Array.isArray(r.findings) ? r.findings.length : 0)
    return (r.ok === false ? 0 : 100) + looked + (r.brand_impersonation || r.credential_harvesting ? 20 : 0)
  }
  return 0
}

// lexicographic: the first differing element decides
function lexGreater(a: number[], b: number[]): boolean {
  for (let j = 0; j < a.length; j++) if (a[j] !== b[j]) return a[j] > b[j]
  return false
}

function pickBestRow(passes: AutoDomainAnalysisPass[], layer: 'http' | 'content'): { row: AutoDomainAnalysisRow; pass: AutoDomainAnalysisPass } | null {
  let best: { row: AutoDomainAnalysisRow; pass: AutoDomainAnalysisPass } | null = null
  let bestKey: number[] = []
  passes.forEach((pass, i) => {
    const row = pass.rows.find((r) => r.analysis_layer === layer)
    if (!row?.raw_result) return
    const classify = pass.rows.find((r) => r.analysis_layer === 'classify')
    // passes are newest-first, so a larger index is an older pass
    const key = [VANTAGE_RANK[pass.vantage ?? 'azure_proxy'] ?? 0, VERDICT_RANK[classify?.verdict ?? ''] ?? -1, rowSubstance(row), i]
    if (!best || lexGreater(key, bestKey)) { best = { row, pass }; bestKey = key }
  })
  return best
}

// "5G · first contact · 16 Sep 11:55" -- which lane looked, whether this is
// the host's evidence of record, when.
function passLabel(pass: AutoDomainAnalysisPass): string {
  return [pass.vantage ? VANTAGE_LABEL[pass.vantage] : 'lane —', pass.isFirstContact ? 'first contact' : 're-analysis', compactDate(pass.createdAt)].join(' · ')
}

function passVerdictLabel(pass: AutoDomainAnalysisPass): string {
  const classify = pass.rows.find((r) => r.analysis_layer === 'classify')
  return classify?.verdict ? VERDICT_LABEL[verdictOf(classify)] : 'no verdict'
}

// Did e33 look at the page this pass classified? A brand verdict reached
// without it rests on the served HTML and the hostname alone (staging
// 2026-10-12: cttentregasexpress.shop, browser timeout, "Brand impersonation"
// off the name + one password field, next to an e33 widget from an earlier
// pass of a different page). why is the layer that stopped it.
const BLIND_VERDICTS = new Set(['brand_lure', 'impersonation_other', 'brand_mention', 'live_other'])
// the candidate-level note is for brand verdicts; the per-pass chip shows on any live one
const BRAND_VERDICTS = new Set(['brand_lure', 'impersonation_other', 'brand_mention'])

function e33Coverage(pass: AutoDomainAnalysisPass): { saw: boolean; why: string | null; needsRender: boolean } {
  const classify = pass.rows.find((r) => r.analysis_layer === 'classify')
  const raw = classify?.raw_result ?? {}
  const needsRender = Boolean(raw.needs_render)
  const content = pass.rows.find((r) => r.analysis_layer === 'content')
  if (raw.content_reused) return { saw: true, why: null, needsRender }
  if (content && content.raw_result?.ok !== false && !content.error) {
    const looked = Array.isArray(content.raw_result?.images_used) ? content.raw_result!.images_used.length > 0 : true
    if (looked) return { saw: true, why: null, needsRender }
    return { saw: false, why: 'e33 had no screenshots to look at', needsRender }
  }
  if (content) return { saw: false, why: `e33 failed${content.error ? `: ${String(content.error).slice(0, 80)}` : ''}`, needsRender }
  const browser = pass.rows.find((r) => r.analysis_layer === 'browser')
  if (!browser) return { saw: false, why: 'the browser did not run', needsRender }
  if (browser.raw_result?.ok === false || browser.error) return { saw: false, why: `browser failed${browser.error || browser.raw_result?.error ? `: ${String(browser.error || browser.raw_result?.error).slice(0, 80)}` : ''}`, needsRender }
  if (!browser.raw_result?.has_screenshots) return { saw: false, why: 'the browser rendered no screenshot', needsRender }
  return { saw: false, why: 'e33 did not run', needsRender }
}

function e33Summary(raw: Record<string, any> | null | undefined): string {
  if (!raw) return '—'
  if (raw.brand_impersonation) return 'impersonation of our brand'
  if (raw.impersonates_other_brand) return 'impersonation of another brand'
  return raw.credential_harvesting ? 'no impersonation, a harvesting form' : 'no impersonation'
}

// The real managed sitekey's domain allowlist does not cover localhost, so the
// invisible widget silently never completes in local dev (no token, no error
// code — just the tiles spinning forever). `next dev` sets NODE_ENV to
// 'development' (a `next build` for qa/staging/prod sets 'production'), so in
// local dev only we fall back to Cloudflare's official always-pass TEST sitekey
// — the real Turnstile flow, issuing a dummy token the backend validates with
// the matching test secret (see getpostimagebykey). Prod is untouched.
const TURNSTILE_TEST_SITEKEY = '1x00000000000000000000AA'
const TURNSTILE_SITEKEY = process.env.NODE_ENV === 'production'
  ? (process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? '')
  : TURNSTILE_TEST_SITEKEY

// One candidate: provenance (which run/strategy/gram found it and why it
// scored what it did), every analysis pass with its per-layer rows, the HTTP
// evidence of the latest pass, the browser pass's screenshots (fetched by
// r2_key through the same Turnstile-gated /api/screenshots/bykey path entry
// screenshots use — no new viewer code), and the three actions. The actions
// are forwarders: Hono authorises and Python does the work
// (backend/routes/backoffice/autoDomains.ts -> jupyter-fraud
// libs/backend/routes/autodomains.py). Promote runs the candidate through
// the normal submission funnel, so after it succeeds the hostname shows up
// in the brand's Unified Entries as an 'unseen' entry with source
// auto_domains.

function fmt(v: unknown): string {
  if (v == null || v === '') return '—'
  if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(3)
  if (typeof v === 'boolean') return v ? 'yes' : 'no'
  if (Array.isArray(v)) return v.length ? v.map(fmt).join(', ') : '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

// One fact. The row follows its own width (a container query, not the
// viewport): side by side with the value on the right when there is room,
// label above value when the widget is a narrow column of the detail grid
// -- the old side-by-side row with break-all wrapped "brand in SLD" one
// letter per line. Values wrap at words; only a token with no break in it
// (a long URL) is split.
function KV({ k, v, c, mono = true }: { k: string; v: React.ReactNode; c: ReturnType<typeof watchClasses>; mono?: boolean }) {
  return (
    <div className="@container/kv py-1">
      <div className="flex flex-col gap-0.5 @xs/kv:flex-row @xs/kv:items-start @xs/kv:justify-between @xs/kv:gap-3">
        <span className={`shrink-0 font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>{k}</span>
        <span className={`min-w-0 break-words [overflow-wrap:anywhere] text-[12px] @xs/kv:text-right ${mono ? 'font-mono' : ''} ${c.text}`}>{v}</span>
      </div>
    </div>
  )
}

// The registrar's abuse desk and the hosting facts, as the takedown report
// will address them (20260915000001). "—" until an analysis pass looked.
function abuseContact(email: string | null | undefined, phone: string | null | undefined) {
  if (!email && !phone) return '—'
  return [email, phone].filter(Boolean).join(' · ')
}

function hostingLine(cand: { hosting_is_cloudflare?: boolean | null; resolved_ips?: string[] | null }) {
  const ips = cand.resolved_ips ?? []
  const front = cand.hosting_is_cloudflare == null ? null : cand.hosting_is_cloudflare ? 'behind Cloudflare' : 'not Cloudflare'
  if (!front && ips.length === 0) return '—'
  return [front, ips.length ? ips.slice(0, 4).join(', ') + (ips.length > 4 ? ` +${ips.length - 4}` : '') : null].filter(Boolean).join(' · ')
}

function layerDetail(row: AutoDomainAnalysisRow) {
  const r = row.raw_result ?? {}
  if (row.analysis_layer === 'whois') return r.ok ? (r.registered === false ? 'not registered' : `${r.created_at ? `created ${String(r.created_at).slice(0, 10)}` : 'registered'}${r.registrar ? ` · ${r.registrar}` : ''}`) : r.error
  if (row.analysis_layer === 'network') return r.ips ? `${(r.ips as string[]).slice(0, 2).join(', ')}` : r.error ?? r.ms != null ? `${r.ms} ms` : null
  if (row.analysis_layer === 'http') return r.status != null ? `HTTP ${r.status}${r.final_host ? ` → ${r.final_host}` : ''}` : r.error
  if (row.analysis_layer === 'browser') return r.ok ? `${r.desktop_is_alive ? 'desktop' : ''}${r.desktop_is_alive && r.mobile_is_alive ? '+' : ''}${r.mobile_is_alive ? 'mobile' : ''} ${r.has_screenshots ? '· shots' : '· no shots'}` : r.error
  if (row.analysis_layer === 'content') {
    if (!r.ok && r.error) return 'e33 failed'
    if (r.brand_impersonation) return 'impersonates ' + (r.impersonated_entity ? String(r.impersonated_entity).replace(/^The page[^.]*imitates\s*/i, '').slice(0, 40) : 'a brand')
    return Array.isArray(r.tags) && r.tags.length ? (r.tags as string[]).filter((t) => t !== 'content_summary' && t !== 'page_state').join(', ').slice(0, 50) || 'no lure signals' : 'no lure signals'
  }
  if (row.analysis_layer === 'classify') return row.verdict ? `${VERDICT_LABEL[verdictOf(row)]} ${row.score != null ? Number(row.score).toFixed(2) : ''}` : null
  return null
}

// The mobile scenario the browser pass actually ran: the session stamps
// browser_type on it -- 'webkit_iphone17' for the real iPhone, anything else
// (the session's own fetcher) for the iPhone User-Agent on desktop Chromium,
// which is also what an iPhone pass silently falls back to without WebKit.
function mobileEngineOf(pass: AutoDomainAnalysisPass): 'iPhone' | 'old mobile' | null {
  for (const r of pass.rows) {
    const bt = r.analysis_layer === 'browser' ? r.raw_result?.postgres_data?.scenarios?.mobile?.browser_type : undefined
    if (bt) return bt === 'webkit_iphone17' ? 'iPhone' : 'old mobile'
  }
  return null
}

function PassCard({ pass, theme, latest }: { pass: AutoDomainAnalysisPass; theme: ProcessedTheme; latest: boolean }) {
  const c = watchClasses(theme)
  const [showJson, setShowJson] = useState(false)
  const order = { network: 0, whois: 1, http: 2, browser: 3, content: 4, classify: 5 } as const
  const rows = [...pass.rows].sort((a, b) => order[a.analysis_layer] - order[b.analysis_layer] || a.test_type.localeCompare(b.test_type))
  const classify = rows.find((r) => r.analysis_layer === 'classify')
  const coverage = e33Coverage(pass)
  const verify = pass.originRun ? verifyRunLabel({ ...pass.originRun, has_candidate_ids: !!pass.originRun.first_candidate_id }) : null
  const mobile = mobileEngineOf(pass)
  // asked for the iPhone, got the spoofed pass: the worker has no WebKit
  const fellBack = mobile === 'old mobile' && pass.originRun?.mobile_engine === 'webkit_iphone17'
  const reasons: string[] = Array.isArray(classify?.raw_result?.reasons) ? (classify!.raw_result!.reasons as string[]) : []
  return (
    <div className={`rounded-md border p-4 ${c.line} ${c.soft}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`font-mono text-[10px] uppercase tracking-[0.24em] ${c.label}`}>{latest ? 'Latest pass' : 'Pass'}</div>
          <div className={`mt-1 font-mono text-sm ${c.text}`}>{compactDate(pass.createdAt)}</div>
          <div className="mt-1 flex flex-wrap gap-1" data-pass-origin>
            {pass.vantage && <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS[VANTAGE_TONE[pass.vantage]]}`} title={VANTAGE_TITLE[pass.vantage]}>{VANTAGE_LABEL[pass.vantage]}</span>}
            {pass.isFirstContact && <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS.emerald}`} title="The oldest pass of this host — the evidence of record; later probes (the monitor) add to it, never erase it">first contact</span>}
            {pass.originRun
              ? <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS.neutral}`} title={`job run ${pass.originRun.id} · ${pass.originRun.job_name}${pass.originRun.requested_by ? ` · ${pass.originRun.requested_by}` : ''}`}>{verify ?? `${pass.originRun.job_name.replace(/^auto_domains\./, '')} · ${pass.originRun.trigger}`}</span>
              : pass.originJobRunId ? null : <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS.neutral}`} title="No job_run recorded for this pass: a notebook or seed pass, or one from before origins were kept">no job</span>}
            {mobile && <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS[fellBack ? 'amber' : 'neutral']}`} data-pass-mobile
              title={fellBack ? 'The run asked for the WebKit iPhone pass but the worker ran the iPhone User-Agent on desktop Chromium: WebKit is missing there' : mobile === 'iPhone' ? 'Mobile scenario: real WebKit as an iPhone 17' : 'Mobile scenario: an iPhone User-Agent on desktop Chromium'}>
              mobile: {mobile}{fellBack ? ' (fallback)' : ''}</span>}
          </div>
          {pass.inherited && (
            <div className={`mt-1 font-mono text-[10px] ${c.muted}`} title="This host has more than one row from the daily-sighting model used before 2026-09-17; the pass, its evidence and screenshots were recorded on that other row">
              probed on another row of this host ({pass.runStartedAt ? compactDate(pass.runStartedAt) : 'an earlier run'})
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {classify && BLIND_VERDICTS.has(classify.verdict ?? '') && !coverage.saw && (
            <span className={`rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS.amber}`} data-pass-blind
              title={`This verdict was reached without e33 looking at the page: ${coverage.why ?? ''}${coverage.needsRender ? '. Needs render: the brand is only in the hostname, so the monitor re-renders it' : ''}`}>
              {coverage.needsRender ? 'needs render' : 'no e33'}
            </span>
          )}
          {classify && (
            <span className={`rounded border px-2 py-1 font-mono text-xs ${c.line} ${c.text}`}>
              {VERDICT_LABEL[verdictOf(classify)]} · {classify.score != null ? Number(classify.score).toFixed(2) : '—'}
            </span>
          )}
          <button onClick={() => setShowJson((v) => !v)} className="font-mono text-[10px] uppercase tracking-widest text-signal hover:text-signal">
            {showJson ? 'Hide' : 'JSON'}
          </button>
        </div>
      </div>
      <div className={`mt-3 flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs ${c.muted}`}>
        {pass.proxyLabel && <span>Proxy {pass.proxyLabel}</span>}
        {rows[0]?.worker_id && <span>Worker {rows[0].worker_id}</span>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {rows.map((r) => {
          const detail = layerDetail(r)
          return (
            <span key={r.id} className={`inline-flex items-center gap-1 rounded border px-2 py-1 font-mono text-xs ${c.line} bg-card ${c.muted}`}>
              <span className={`h-2 w-2 rounded-full ${r.is_alive === true ? 'bg-success' : r.is_alive === false ? 'bg-destructive' : 'bg-subtle'}`} />
              {r.analysis_layer.toUpperCase()}/{r.test_type}
              {detail != null && <span className={c.faint}>· {String(detail).slice(0, 60)}</span>}
            </span>
          )
        })}
      </div>
      {reasons.length > 0 && (
        <ul className={`mt-2 list-disc pl-5 text-[11px] leading-relaxed ${c.muted}`}>
          {reasons.map((reason, i) => <li key={i}>{reason}</li>)}
        </ul>
      )}
      {showJson && (
        <pre className={`mt-3 max-h-64 overflow-y-auto rounded-md border p-3 text-[10px] leading-relaxed border-border bg-secondary text-muted-foreground`}>
          {JSON.stringify(rows, null, 2)}
        </pre>
      )}
    </div>
  )
}

// Screenshots of the browser passes, the way Last obtained images shows an
// entry's captures: the latest pass that stored screenshots fills the two
// tiles, every pass sits in a strip of thumbnails below (one fetch per
// r2_key, only once the thumbnail scrolls into view), and clicking one
// brings that pass up into the tiles -- both scenarios, its alive/down
// line, its findings. A pass that rendered nothing is in the strip too, as
// a blank with its reason, so "the latest probe got nothing" is visible
// without hiding the last real capture. Each fetch is the Turnstile-gated
// /api/screenshots/bykey round trip, serialised through getToken.
type Shots = { desktop: string | null; mobile: string | null }
type ShotState = 'loading' | 'error' | 'no-data' | Shots
const shotsOf = (st: ShotState | undefined): Shots | null => (st && typeof st === 'object' ? st : null)

function noShotsWhy(row: AutoDomainAnalysisRow): string | null {
  if (row.r2_key) return null
  return row.raw_result?.has_screenshots
    ? 'The session captured screenshots but they were not written to R2 — check the bucket credentials on the worker'
    : 'The browser session produced no screenshots at all; nothing was there to store. A session that returns in a second usually means the browser itself is missing or failed to start on the worker.'
}

function PassThumb({ row, state, selected, onVisible, onPick, theme }: {
  row: AutoDomainAnalysisRow; state: ShotState | undefined; selected: boolean
  onVisible: (row: AutoDomainAnalysisRow) => void; onPick: (row: AutoDomainAnalysisRow) => void; theme: ProcessedTheme
}) {
  const c = watchClasses(theme)
  const ref = useRef<HTMLButtonElement>(null)
  const label = compactDateNumeric(row.created_at)
  const img = shotsOf(state)
  const src = img?.desktop ?? img?.mobile ?? null
  const none = !row.r2_key
  useEffect(() => {
    const el = ref.current
    if (!el || state || none) return
    const observer = new IntersectionObserver((entries) => { if (entries[0]?.isIntersecting) onVisible(row) }, { rootMargin: '150px' })
    observer.observe(el)
    return () => observer.disconnect()
  }, [row, state, none, onVisible])
  const r = row.raw_result ?? {}
  const title = none ? `${label} — no screenshots: ${noShotsWhy(row)}`
    : state === 'error' ? `${label} — failed to load, click to retry`
    : `${label} · desktop ${r.desktop_is_alive ? 'alive' : 'down'} · mobile ${r.mobile_is_alive ? 'alive' : 'down'}${row.vantage ? ` · ${VANTAGE_LABEL[row.vantage]}` : ''}`
  return (
    <button ref={ref} type="button" data-pass-thumb={row.id} data-selected={selected || undefined} title={title}
      onClick={() => { onPick(row); if (!src && !none) onVisible(row) }}
      className={`relative aspect-[4/5] w-16 shrink-0 overflow-hidden rounded border transition-colors ${selected ? 'border-signal' : c.line} ${c.soft} hover:border-signal`}>
      {src ? (
        <img src={src} alt={label} className="h-full w-full object-cover object-top" />
      ) : (
        <span className={`flex h-full w-full items-center justify-center font-mono text-[9px] ${state === 'error' ? 'text-destructive' : none ? 'text-warning' : c.faint}`}>
          {none ? 'none' : state === 'loading' ? '···' : state === 'error' ? 'retry' : state === 'no-data' ? 'empty' : '↓'}
        </span>
      )}
      <span className="absolute inset-x-0 bottom-0 truncate bg-overlay px-0.5 py-0.5 text-center font-mono text-[7px] text-white">{label}</span>
    </button>
  )
}

function CandidateScreenshots({ rows, theme, getToken }: { rows: AutoDomainAnalysisRow[]; theme: ProcessedTheme; getToken: () => Promise<string> }) {
  const c = watchClasses(theme)
  // rows are newest-first (passes are); the tiles show the newest pass that
  // stored screenshots, or the newest pass at all when none did
  const latestStored = useMemo(() => rows.find((r) => r.r2_key) ?? null, [rows])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const shown = rows.find((r) => r.id === selectedId) ?? latestStored ?? rows[0] ?? null
  const [shots, setShots] = useState<Record<string, ShotState>>({})
  const inFlight = useRef<Set<string>>(new Set())
  const [lightbox, setLightbox] = useState<{ src: string; label: string } | null>(null)

  const load = useCallback((row: AutoDomainAnalysisRow) => {
    if (!row.r2_key) { setShots((prev) => ({ ...prev, [row.id]: 'no-data' })); return }
    if (inFlight.current.has(row.id)) return
    inFlight.current.add(row.id)
    setShots((prev) => ({ ...prev, [row.id]: 'loading' }))
    ;(async () => {
      try {
        const token = await getToken()
        const res = await fetch('/api/screenshots/bykey', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ r2Key: row.r2_key, turnstileToken: token }),
        })
        const result = await res.json()
        const got = result.success ? extractScreenshotImages(result.data) : null
        if (!got?.desktop && !got?.mobile) {
          inFlight.current.delete(row.id)
          setShots((prev) => ({ ...prev, [row.id]: result.success ? 'no-data' : 'error' }))
          return
        }
        setShots((prev) => ({ ...prev, [row.id]: { desktop: got.desktop, mobile: got.mobile } }))
      } catch {
        inFlight.current.delete(row.id)
        setShots((prev) => ({ ...prev, [row.id]: 'error' }))
      }
    })()
  }, [getToken])

  // the pass in the tiles loads at once; the strip loads as it scrolls
  useEffect(() => { if (shown && !shots[shown.id]) load(shown) }, [shown, shots, load])

  if (!shown) return null
  const r = shown.raw_result ?? {}
  const state = shots[shown.id]
  const img = shotsOf(state)
  const retry = () => { inFlight.current.delete(shown.id); load(shown) }
  const fallback = state === 'loading' || state === undefined ? 'Loading' : state === 'error' ? <button onClick={retry} className="text-destructive">Retry</button> : 'No image'
  const newest = rows[0]
  const newerThanShown = newest && newest.id !== shown.id && newest.created_at > shown.created_at
  return (
    <div data-browser-row={shown.id}>
      {lightbox && <ScreenshotLightbox src={lightbox.src} label={lightbox.label} onClose={() => setLightbox(null)} />}
      {newerThanShown && (
        <div className={`mb-2 font-mono text-[9px] uppercase tracking-widest ${c.faint}`} title={noShotsWhy(newest) ?? 'A later pass exists; click its thumbnail to bring it up'}>
          Latest with screenshots · {compactDate(shown.created_at)} · later probe {compactDate(newest.created_at)} {newest.r2_key ? '' : 'rendered none'}
        </div>
      )}
      <div className={`mb-2 flex flex-wrap items-center gap-2 font-mono text-[10px] ${c.faint}`}>
        <span>{compactDate(shown.created_at)}</span>
        {shown.vantage && <span className={`rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${CHIP_TONE_CLASS[VANTAGE_TONE[shown.vantage]]}`} title={VANTAGE_TITLE[shown.vantage]}>{VANTAGE_LABEL[shown.vantage]}</span>}
        <span className={r.desktop_is_alive ? 'text-success' : 'text-destructive'}>desktop {r.desktop_is_alive ? 'alive' : 'down'}</span>
        <span className={r.mobile_is_alive ? 'text-success' : 'text-destructive'}>mobile {r.mobile_is_alive ? 'alive' : 'down'}</span>
        {shown.proxy_label && <span>via {shown.proxy_label}</span>}
        {!shown.r2_key && <span className="text-warning" title={noShotsWhy(shown) ?? undefined}>{r.has_screenshots ? 'captured, not stored' : 'no screenshots captured'}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ImageTile label="Mobile" src={img?.mobile ?? null} caption={compactDateNumeric(shown.created_at)} fallback={fallback} onExpand={() => img?.mobile && setLightbox({ src: img.mobile, label: `Mobile · ${compactDate(shown.created_at)}` })} />
        <ImageTile label="Desktop" src={img?.desktop ?? null} caption={compactDateNumeric(shown.created_at)} fallback={fallback} onExpand={() => img?.desktop && setLightbox({ src: img.desktop, label: `Desktop · ${compactDate(shown.created_at)}` })} />
      </div>
      {Array.isArray(r.security_findings) && r.security_findings.length > 0 && (
        <ul className={`mt-2 list-disc pl-5 text-[11px] leading-relaxed ${c.muted}`}>
          {r.security_findings.slice(0, 8).map((f: any, i: number) => <li key={i}>{typeof f === 'string' ? f : f.description ?? f.type ?? JSON.stringify(f)}</li>)}
        </ul>
      )}
      {rows.length > 1 && (
        <div className="mt-3">
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>
            Passes · {rows.length} · {rows.filter((x) => x.r2_key).length} with screenshots
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {rows.map((row) => (
              <PassThumb key={row.id} row={row} state={shots[row.id]} selected={row.id === shown.id} onVisible={load} onPick={(x) => setSelectedId(x.id)} theme={theme} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function DomainCandidateDetail({
  candidateId,
  theme,
  onClose,
  onChanged,
  onOpenCampaign,
  isEditor,
}: {
  candidateId: string
  theme: ProcessedTheme
  onClose: () => void
  onChanged: () => void
  /** Drill-through to the campaign a known candidate's domain already belongs to. */
  onOpenCampaign?: (campaignId: string) => void
  isEditor?: boolean
}) {
  const c = watchClasses(theme)
  // One row per host (since 2026-09-17); the id shown is the prop.
  const currentId = candidateId
  const [data, setData] = useState<DetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const screenshotTurnstileRef = useRef<TurnstileHandle>(null)
  const pendingTokenRef = useRef<((token: string) => void) | null>(null)
  const pendingRejectRef = useRef<((err: Error) => void) | null>(null)
  const handleScreenshotVerify = useCallback((token: string) => {
    pendingTokenRef.current?.(token)
    pendingTokenRef.current = null
    pendingRejectRef.current = null
  }, [])
  // Without this, a Turnstile that errors or times out leaves the token promise
  // pending and the screenshot tiles show "Loading" forever. Reject instead so
  // the screenshot tiles fall to their 'error' state and offer a Retry. Stable
  // identities: the <Turnstile> effect re-renders the widget whenever a callback
  // prop changes, so a fresh inline function each render would tear the widget
  // down mid-challenge ("Cannot find Widget ...").
  const handleScreenshotFail = useCallback((why: string) => {
    pendingRejectRef.current?.(new Error(`turnstile ${why}`))
    pendingTokenRef.current = null
    pendingRejectRef.current = null
  }, [])
  const handleScreenshotError = useCallback(() => handleScreenshotFail('error'), [handleScreenshotFail])
  const handleScreenshotTimeout = useCallback(() => handleScreenshotFail('timeout'), [handleScreenshotFail])
  // One shared invisible Turnstile widget; one pending promise at a time —
  // the screenshot loads below are serialised through this queue so a second
  // request can't clobber the first's pending resolver.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve())
  const getScreenshotToken = useCallback((): Promise<string> => {
    const task = () => new Promise<string>((resolve, reject) => {
      pendingTokenRef.current = resolve
      pendingRejectRef.current = reject
      const tryRun = (attempt = 0) => {
        if (screenshotTurnstileRef.current) {
          screenshotTurnstileRef.current.reset()
          screenshotTurnstileRef.current.run()
          return
        }
        if (attempt < 20) { setTimeout(() => tryRun(attempt + 1), 100); return }
        // widget never mounted -> don't hang the promise
        reject(new Error('turnstile widget unavailable'))
        pendingTokenRef.current = null
        pendingRejectRef.current = null
      }
      tryRun()
    })
    // Turnstile can silently never complete on some origins (local dev): the
    // error/timeout callbacks don't fire, so guard the token with a hard
    // deadline or the screenshot tiles spin forever.
    const withDeadline = () => new Promise<string>((resolve, reject) => {
      const t = setTimeout(() => {
        pendingTokenRef.current = null
        pendingRejectRef.current = null
        reject(new Error('turnstile token deadline (no challenge completion)'))
      }, 15000)
      task().then((tok) => { clearTimeout(t); resolve(tok) }, (e) => { clearTimeout(t); reject(e) })
    })
    const run = queueRef.current.then(withDeadline, withDeadline)
    queueRef.current = run.then(() => undefined, () => undefined)
    return run
  }, [])

  // `quiet` (the pass poll below): a failed fetch keeps the page as it is
  // instead of swapping it for the error screen.
  const load = useCallback(async (quiet = false): Promise<DetailData | null> => {
    setLoading(true)
    if (!quiet) setError(null)
    const result = await apiGet<DetailData>(`/auto-domains/candidates/${currentId}`)
    if (result.success && result.data) setData(result.data)
    else if (!quiet) setError(result.error ?? 'Failed to load candidate')
    setLoading(false)
    return result.success ? result.data ?? null : null
  }, [currentId])

  useEffect(() => { load() }, [load])

  // The timeline and Actions-taken widgets fetch on their own; a bump of
  // refreshKey makes them refetch with the candidate. Not on mount (they
  // already fetch then).
  const [refreshKey, setRefreshKey] = useState(0)
  const reload = useCallback(async (quiet = false) => {
    setRefreshKey((k) => k + 1)
    return load(quiet)
  }, [load])

  // A Verify only queues the pass; the worker runs it a few seconds to
  // minutes later. Poll the candidate until a pass we had not seen carries
  // its classify row (the last one a pass writes), then refresh everything.
  // Gives up after 15 min -- a worker that is down leaves the Reload button.
  const onChangedRef = useRef(onChanged)
  onChangedRef.current = onChanged
  const pollRef = useRef<{ timer: ReturnType<typeof setTimeout> | null; gen: number }>({ timer: null, gen: 0 })
  const [awaitingPass, setAwaitingPass] = useState(false)
  const stopPoll = useCallback(() => {
    if (pollRef.current.timer) clearTimeout(pollRef.current.timer)
    pollRef.current = { timer: null, gen: pollRef.current.gen + 1 }
    setAwaitingPass(false)
  }, [])
  const awaitNewPass = useCallback((seen: ReadonlySet<string>) => {
    stopPoll()
    const gen = pollRef.current.gen
    const started = Date.now()
    setAwaitingPass(true)
    const tick = async () => {
      const d = await load(true)
      if (pollRef.current.gen !== gen) return
      const landed = !!d?.passes.some((p) => !seen.has(p.passId) && p.rows.some((r) => r.analysis_layer === 'classify'))
      const elapsed = Date.now() - started
      if (landed || elapsed > 15 * 60_000) {
        stopPoll()
        if (landed) { setRefreshKey((k) => k + 1); onChangedRef.current() }
        return
      }
      pollRef.current.timer = setTimeout(tick, elapsed < 2 * 60_000 ? 5_000 : 15_000)
    }
    pollRef.current.timer = setTimeout(tick, 5_000)
  }, [load, stopPoll])
  useEffect(() => stopPoll, [currentId, stopPoll])

  const cand = data?.candidate
  const latestPass = data?.passes[0]
  const bestHttp = useMemo(() => pickBestRow(data?.passes ?? [], 'http'), [data])
  const bestContent = useMemo(() => pickBestRow(data?.passes ?? [], 'content'), [data])
  const httpRow = bestHttp?.row
  const contentRow = bestContent?.row
  const firstContact = useMemo(() => (data?.passes ?? []).find((p) => p.isFirstContact) ?? null, [data])
  // The pass whose verdict is the candidate's (keep-best stores the newest
  // pass that tied or beat the stored one, from its lane), and whether e33 saw it.
  const verdictPass = useMemo(() => (data?.passes ?? []).find((p) => {
    const cl = p.rows.find((r) => r.analysis_layer === 'classify')
    return cl?.verdict != null && cl.verdict === data?.candidate.verdict
      && (!data?.candidate.verdict_vantage || p.vantage === data.candidate.verdict_vantage)
  }) ?? null, [data])
  const verdictCoverage = verdictPass ? e33Coverage(verdictPass) : null
  const whoisRow = useMemo(() => (data?.passes ?? []).flatMap((p) => p.rows).find((r) => r.analysis_layer === 'whois' && r.raw_result?.ok), [data])
  const [showWhoisRaw, setShowWhoisRaw] = useState(false)
  const browserRows = useMemo(() => (data?.passes ?? []).flatMap((p) => p.rows.filter((r) => r.analysis_layer === 'browser')), [data])
  const primaryGram = useMemo(() => data?.grams.find((g) => g.gram_id === cand?.gram_id) ?? null, [data, cand])
  const otherGrams = useMemo(() => (data?.grams ?? []).filter((g) => g.gram_id !== cand?.gram_id), [data, cand])

  // The three verifies are one /reanalyze call. 'reanalyze' is the standard:
  // the 5G lane with the WebKit iPhone 17 mobile pass (the server default).
  // 'reanalyze-spoofed' keeps the lane but asks for the older mobile pass (an
  // iPhone User-Agent on the desktop Chromium); 'reanalyze-azure' queues the
  // standard pass on the proxied Azure lane instead. They live in the
  // sidebar (the same InvestigationSidebar an entry has), whatever the
  // candidate's status: a promoted one is verified like any other and stays
  // promoted.
  type Verify = 'reanalyze' | 'reanalyze-spoofed' | 'reanalyze-azure'
  const VERIFY_BODY: Record<Verify, Record<string, unknown>> = {
    reanalyze: { lane: '5g', with_browser: true },
    'reanalyze-spoofed': { lane: '5g', with_browser: true, mobile_engine: 'spoofed' },
    'reanalyze-azure': { lane: 'azure_proxy', with_browser: true },
  }
  const runVerify = async (kind: Verify): Promise<InvestigationActionResult> => {
    if (!cand) return { success: false, error: 'Candidate not loaded.' }
    const seen = new Set((data?.passes ?? []).map((p) => p.passId))
    const result = await apiPost<any>(`/auto-domains/candidates/${cand.candidate_id}/reanalyze`, VERIFY_BODY[kind])
    if (!result.success) return { success: false, error: result.error ?? 'verify failed' }
    await reload()
    onChanged()
    awaitNewPass(seen)
    const run = result.data?.run_id ? ` (run ${String(result.data.run_id).slice(0, 8)})` : ''
    return {
      success: true,
      message: kind === 'reanalyze-azure'
        ? `Queued on the Azure lane as an iPhone 17${run} — the proxied worker picks it up on its next poll; this page refreshes when the pass lands. A 5G verdict outranks it.`
        : `Queued on the 5G lane${kind === 'reanalyze-spoofed' ? ' with the old mobile pass (Chrome as iPhone)' : ' as an iPhone 17'}${run} — the local worker picks it up on its next poll; this page refreshes when the pass lands. Its verdict replaces the Azure one; a lesser result never replaces a better one.`,
    }
  }

  // Promote / unpromote / discard: the decisions, with their confirm (and the
  // discard reason) asked by the sidebar row; the outcome is the run's note.
  const runDecision = async (kind: 'promote' | 'unpromote' | 'discard', reason?: string): Promise<InvestigationActionResult> => {
    if (!cand) return { success: false, error: 'Candidate not loaded.' }
    const result = await apiPost<any>(`/auto-domains/candidates/${cand.candidate_id}/${kind}`, kind === 'discard' ? { reason: reason ?? '' } : {})
    if (!result.success) return { success: false, error: result.error ?? `${kind} failed` }
    await reload()
    onChanged()
    return {
      success: true,
      message: kind === 'promote'
        ? (result.data?.already_promoted ? 'Already promoted — nothing new created.' : `Promoted — entry ${result.data?.user_entry_id ?? ''} created (classification pending).`)
        : kind === 'unpromote'
          ? `Unpromoted — entry ${result.data?.user_entry_id ?? ''} deleted; ${result.data?.detached?.length ?? 1} candidate row${(result.data?.detached?.length ?? 1) === 1 ? '' : 's'} back to analyzed. Discard it if it should not come back.`
          : 'Discarded.',
    }
  }

  // A verdict set by hand: the bin the operator picks, with a note saying
  // why. Held against later automatic passes (keep-best); not a promotion.
  const runSetVerdict = async (note?: string, verdict?: string): Promise<InvestigationActionResult> => {
    if (!cand) return { success: false, error: 'Candidate not loaded.' }
    if (!verdict) return { success: false, error: 'Pick a bin.' }
    const result = await apiPost<any>(`/auto-domains/candidates/${cand.candidate_id}/verdict`, { verdict, note: note ?? '' })
    if (!result.success) return { success: false, error: result.error ?? 'set verdict failed' }
    await reload()
    onChanged()
    return {
      success: true,
      message: `Verdict set to ${VERDICT_LABEL[verdict as CandidateVerdict] ?? verdict} by hand — it holds against later passes. ${verdict === 'brand_lure' ? 'Promote it if it should become an entry.' : ''}`.trim(),
    }
  }

  const copy = async (label: string, value: string) => {
    try { await navigator.clipboard.writeText(value); setCopied(label); setTimeout(() => setCopied(null), 1200) } catch {}
  }

  // The promoted entry's classification: the same steps and the same
  // evidence panel the entry detail shows, so the reports' inputs (phones,
  // references, the investigator's explanation) are visible where the
  // reports are.
  const entrySteps = useMemo(() => data?.pipelineSteps ?? [], [data])
  const entryEvidence = useMemo(() => deriveActionableEvidence(entrySteps), [entrySteps])

  if (loading && !data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse ${c.faint}`}>Loading…</span>
      </div>
    )
  }
  if (error || !cand) {
    return (
      <div className={`flex h-64 flex-col items-center justify-center gap-3 text-destructive`}>
        <AlertCircle className="h-5 w-5" />
        <span className="font-mono text-[13px]">{error ?? 'Candidate not found'}</span>
        <button onClick={onClose} className="font-mono text-[11px] uppercase tracking-widest text-signal">Back</button>
      </div>
    )
  }

  const v = verdictOf(cand)
  const known = data?.knownEntries ?? []
  const knownCampaign = known.find((k) => k.campaign_id)
  const badges: DetailBadge[] = [
    { label: STATUS_LABEL[cand.status], tone: STATUS_TONE[cand.status] },
    ...(aliveForDays(cand) != null ? [{ label: `alive ${aliveForDays(cand)} d`, tone: 'red' as const }] : []),
    // A Brand impersonation says what produced it: only "e33 saw the brand"
    // is the criterion (2026-10-17); a weaker or legacy basis is a stamp the
    // replay / the next rendered pass will settle.
    { label: `${VERDICT_LABEL[v]}${cand.verdict_score != null ? ` · ${Number(cand.verdict_score).toFixed(2)}` : ''}${cand.verdict_vantage ? ` · ${VANTAGE_LABEL[cand.verdict_vantage]}` : ''}${v === 'brand_lure' ? ` · ${cand.verdict_basis ? BASIS_LABEL[cand.verdict_basis] : LEGACY_BASIS_LABEL}` : ''}`, tone: VERDICT_TONE[v] },
    ...(cand.last_probe_verdict && cand.last_probe_verdict !== cand.verdict
      ? [{ label: `now: ${VERDICT_LABEL[cand.last_probe_verdict]}${cand.last_probe_vantage ? ` · ${VANTAGE_LABEL[cand.last_probe_vantage]}` : ''}${cand.last_probe_at ? ` · ${fmtAge(cand.last_probe_at) ?? ''}` : ''}`, tone: 'neutral' as const }]
      : []),
    // Like the list: the brand in the registered name is every candidate's
    // normal case, only a brand in a subdomain is worth a badge.
    ...(cand.brand_position === 'subdomain' || cand.brand_position === 'both'
      ? [{ label: BRAND_POSITION_LABEL[cand.brand_position], tone: 'amber' as const }]
      : []),
    ...(cand.is_known
      ? [{ label: `already flagged · ${cand.known_entry_count} ${cand.known_entry_count === 1 ? 'entry' : 'entries'}`, tone: 'emerald' as const }]
      : cand.status === 'promoted'
        ? [{ label: 'new when sighted · now an entry', tone: 'emerald' as const }]
        : []),
  ]
  const promoteLabel = cand.is_known ? 'Record re-sighting' : 'Promote'

  const canAct = isEditor !== false
  const undecided = cand.status !== 'promoted' && cand.status !== 'discarded'
  const actionBtn = 'flex items-center gap-1.5 rounded border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50'
  const analyzing = cand.status === 'analyzing' ? 'A pass is already running on this candidate — wait for it to finish, then reload.' : undefined
  const headerActions = (
    <button onClick={() => reload()} title={awaitingPass ? 'Waiting for the queued pass — the page refreshes when it lands' : 'Reload'} className={`${actionBtn} ${c.line} ${c.faint}`}>
      <RefreshCw className={`h-3 w-3 ${loading || awaitingPass ? 'animate-spin' : ''}`} />
      {awaitingPass && <span data-awaiting-pass>Waiting for pass</span>}
    </button>
  )
  // The page's actions, in the sidebar like an entry's. Decide: the confirm
  // steps the header used to hold. Verify: the three passes, whatever the
  // status -- a promoted candidate is verified like any other and stays promoted.
  const actions: InvestigationAction[] = [
    ...(undecided ? [{
      id: 'promote', group: 'Decide', label: promoteLabel, icon: ExternalLink, tone: 'emerald' as const, requiresTarget: false,
      dataAction: 'promote', confirm: `Confirm ${cand.is_known ? 're-sighting' : 'promote'}`,
      description: cand.is_known ? 'Record this sighting on the existing entry' : 'Create an entry from this candidate; classification follows',
      run: () => runDecision('promote'),
    }] : []),
    // Undo of a promotion (an auto-promote that got it wrong, mostly): Python
    // deletes the entry and its planned reports and returns the candidate to
    // analyzed. Editor only, refused once a report went out.
    ...(cand.status === 'promoted' && isEditor ? [{
      id: 'unpromote', group: 'Decide', label: 'Unpromote', icon: Undo2, tone: 'amber' as const, requiresTarget: false,
      dataAction: 'unpromote', confirm: 'Confirm unpromote — deletes the entry',
      description: 'Delete the entry this candidate became and put the candidate back to analyzed. Refused once a report for it has been sent.',
      run: () => runDecision('unpromote'),
    }] : []),
    // The bin by hand (2026-10-18): any verdict, when the analysis got it
    // wrong -- a CTT login page the browser rendered but a 403 kept in
    // 'blocked'. Editor only; a discarded row is refused by Python.
    ...(cand.status !== 'discarded' && isEditor ? [{
      id: 'set-verdict', group: 'Decide', label: 'Set verdict', icon: ShieldAlert, tone: 'amber' as const, requiresTarget: false,
      dataAction: 'set-verdict', confirm: 'Confirm verdict',
      choice: { label: 'Bin', options: VERDICT_ORDER.map((v) => ({ value: v, label: VERDICT_LABEL[v] })), initial: 'brand_lure' },
      input: { placeholder: 'Why, e.g. screenshot shows a CTT login clone', minLength: 2 },
      description: cand.verdict_set_by ? `Set by hand by ${cand.verdict_set_by}; pick again to change it` : 'Put this candidate in a bin by hand; it holds against later passes',
      run: (_: string, note?: string, choice?: string) => runSetVerdict(note, choice),
    }] : []),
    ...(undecided ? [{
      id: 'discard', group: 'Decide', label: 'Discard', icon: Trash2, tone: 'red' as const, requiresTarget: false,
      dataAction: 'discard', confirm: 'Confirm discard', input: { placeholder: 'Reason, e.g. legit partner lookalike', minLength: 2 },
      description: 'Not a lure: keep it out of the list, with a reason',
      run: (_: string, reason?: string) => runDecision('discard', reason),
    }] : []),
    {
      id: 'reanalyze', group: 'Verify', label: 'Verify on 5G (iPhone)', icon: Smartphone, tone: 'sky', requiresTarget: false,
      dataAction: 'reanalyze', disabledReason: analyzing,
      description: 'The standard pass, from the 5G laptop: a desktop Chrome and a real iPhone 17 Safari (WebKit)',
      run: () => runVerify('reanalyze'),
    },
    {
      id: 'reanalyze-spoofed', group: 'Verify', label: 'Verify on 5G (old mobile)', icon: RefreshCw, tone: 'sky', requiresTarget: false,
      dataAction: 'reanalyze-spoofed', disabledReason: analyzing,
      description: 'The old pass: the mobile scenario is the desktop Chrome pretending to be an iPhone (only the User-Agent changes) — kits that check more than that see a desktop bot',
      run: () => runVerify('reanalyze-spoofed'),
    },
    {
      id: 'reanalyze-azure', group: 'Verify', label: 'Verify via proxy (iPhone)', icon: Globe, tone: 'sky', requiresTarget: false,
      dataAction: 'reanalyze-azure', disabledReason: analyzing,
      description: 'The standard pass (iPhone 17 Safari) from the Azure container through a Webshare PT proxy — a datacenter IP, so a kit that only answers carriers may still hide',
      run: () => runVerify('reanalyze-azure'),
    },
  ]

  return (
    <>
      <Turnstile ref={screenshotTurnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify}
        onError={handleScreenshotError} onTimeout={handleScreenshotTimeout} />
      <EntryDetailShell
        mode="page"
        open
        onClose={onClose}
        theme={theme}
        title={cand.hostname}
        subtitle={`${cand.registrable_domain ?? ''} · ${cand.strategy_code ?? 'manual'} / ${cand.gram ?? '—'} · run ${compactDate(cand.run_started_at)} (${cand.run_start_kind})`}
        badges={badges}
        cols={3}
        headerActions={headerActions}
        sidebar={canAct ? (
          <InvestigationSidebar
            theme={theme}
            title="Actions"
            target={cand.hostname}
            onTargetChange={() => {}}
            showTarget={false}
            actions={actions}
          />
        ) : undefined}
      >
        {verdictPass && verdictCoverage && !verdictCoverage.saw && BRAND_VERDICTS.has(cand.verdict ?? '') && (
          <div className={`${detailColSpan(3)} border p-3 ${CHIP_TONE_CLASS.amber}`} data-verdict-blind>
            <div className="font-mono text-[10px] uppercase tracking-widest">
              Verdict reached without e33{verdictCoverage.needsRender ? ' · needs render' : ''}
            </div>
            <div className="mt-1 text-[12px] leading-relaxed">
              {VERDICT_LABEL[v]} comes from the pass {passLabel(verdictPass)}, where {verdictCoverage.why ?? 'e33 did not run'}: it rests on the served HTML
              {cand.brand_position !== 'none' ? ' and the brand in the hostname' : ''}, not on what the page looks like.
              {' '}{bestContent && bestContent.pass.passId !== verdictPass.passId && bestContent.row.raw_result?.ok !== false && !bestContent.row.error
                ? <>e33 last looked on {passLabel(bestContent.pass)} and saw {e33Summary(bestContent.row.raw_result)}; that may be a different page.</>
                : <>e33 has not looked at this host yet.</>}
              {verdictCoverage.needsRender ? ' The brand is only in the hostname, so the monitor re-renders it on its next pass.' : ''}
            </div>
          </div>
        )}

        {(cand.is_known || cand.user_entry_id) && (
          <div className={detailColSpan(3)}>
            <Widget
              label={cand.is_known
                ? <>Already flagged · {cand.known_entry_count} existing {cand.known_entry_count === 1 ? 'entry' : 'entries'}</>
                : <>Promoted · the entry this candidate became</>}
              theme={theme} labelClassName="text-success" borderClassName="border-success/40"
            >
              <p className={`mb-3 text-[12px] leading-relaxed ${c.muted}`}>
                {cand.is_known
                  ? 'This hostname is already in the domain table — the sourcing run re-sighted something the backoffice holds, it did not discover it.'
                  : 'This hostname was new when sighted; promoting it created the entry below, which now flows like any report (Unified Entries, classification, campaign).'}
                {knownCampaign?.campaign_id ? ` It belongs to a campaign with ${knownCampaign.campaign_entry_count ?? '?'} report${(knownCampaign.campaign_entry_count ?? 0) === 1 ? '' : 's'}.` : ''}
                {' '}{cand.is_known && promoteLabel === 'Record re-sighting' ? 'Recording a re-sighting adds one more report to that campaign (source auto_domains) rather than creating a new case.' : ''}
              </p>
              {known.length > 0 ? (
                <div className="space-y-1.5">
                  {known.map((k) => (
                    <div key={k.user_entry_id} className={`flex flex-wrap items-center justify-between gap-2 rounded border px-3 py-2 ${c.line} ${c.soft}`}>
                      <div className="flex flex-wrap items-center gap-2 font-mono text-[11px]">
                        {k.via_promotion && <span className="rounded border border-success/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-success">this promotion</span>}
                        <span className={c.text}>{k.platform_name ?? '—'}</span>
                        <span className={c.faint}>{k.source ?? '—'}</span>
                        <span className={c.faint}>{k.raw_entry_created_at ? compactDate(k.raw_entry_created_at) : '—'}</span>
                        <span className={`rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${k.revision_status === 'accept' || k.revision_status === 'auto-accept' ? 'border-success/40 text-success' : k.revision_status === 'reject' ? 'border-destructive/40 text-destructive' : 'border-border text-muted-foreground'}`}>{k.revision_status ?? 'unseen'}</span>
                        {k.is_scam && <span className="rounded border border-destructive/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-destructive">scam</span>}
                        {k.campaign_id && <span className={c.faint}>campaign · {k.campaign_entry_count ?? 1} report{(k.campaign_entry_count ?? 1) === 1 ? '' : 's'} · {k.campaign_type ?? ''}</span>}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {/* The entry's own page, in a new tab: this candidate
                            stays open here, and the link carries the id. */}
                        <a data-action="open-entry" href={siblingDetailHref('unified', k.user_entry_id)} target="_blank" rel="noopener noreferrer" className={`${actionBtn} border-signal/30 text-signal hover:bg-secondary`}>
                          <ExternalLink className="h-3 w-3" /> Open entry
                        </a>
                        {k.campaign_id && onOpenCampaign && (
                          <button data-action="open-campaign" onClick={() => onOpenCampaign(k.campaign_id!)} className={`${actionBtn} border-signal/30 text-signal hover:bg-secondary`}>
                            <ExternalLink className="h-3 w-3" /> Open campaign
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {cand.known_entry_count > known.filter((k) => !k.via_promotion).length && <div className={`font-mono text-[10px] ${c.faint}`}>+{cand.known_entry_count - known.filter((k) => !k.via_promotion).length} older entries not listed</div>}
                </div>
              ) : (
                <div className={`font-mono text-[11px] ${c.faint}`}>domain_id {cand.domain_id} is in the domain table but no entry currently links it.</div>
              )}
            </Widget>
          </div>
        )}

        {/* The takedown reports for the promoted entry -- the same widget the
            entry detail shows, keyed on the user_entry_id promotion wrote
            back. Before promotion there is no entry to report from. */}
        <div className={detailColSpan(3)}>
          <Widget label="Actions taken" theme={theme}>
            {cand.user_entry_id ? (
              <ActionsWidget userEntryId={cand.user_entry_id} hostname={cand.hostname} theme={theme} refreshKey={refreshKey} />
            ) : (
              <span className={`font-mono text-[11px] ${c.faint}`}>Promote the candidate first — reports are filed for the entry it becomes.</span>
            )}
          </Widget>
        </div>

        {/* Before promotion the timeline is the candidate's own: sighted,
            each analysis pass with its lane, the verdict, WHOIS, a discard.
            After promotion the entry's timeline carries those same rows, so
            the widget switches key rather than showing two. */}
        {!cand.user_entry_id && (
          <div className={detailColSpan(3)}>
            <EntryTimelineWidget candidateId={candidateId} theme={theme} refreshKey={refreshKey} className="" label={<>Timeline · candidate {candidateId.slice(0, 8)}</>} />
          </div>
        )}

        {cand.user_entry_id && (
          <div className={`${detailColSpan(3)} grid gap-3 sm:grid-cols-2`} data-entry-classification>
            <EntryTimelineWidget userEntryId={cand.user_entry_id} theme={theme} refreshKey={refreshKey} className="sm:col-span-2" label={<>Timeline · entry {cand.user_entry_id.slice(0, 8)}</>} />
            <Widget label={<>Actionable evidence · entry {cand.user_entry_id.slice(0, 8)}</>} theme={theme}>
              {entrySteps.length === 0 ? (
                <span className={`font-mono text-[11px] ${c.faint}`}>No pipeline steps recorded yet — classification runs right after promotion; refresh in a minute.</span>
              ) : (
                <ActionableEvidencePanel evidence={entryEvidence} theme={theme} />
              )}
            </Widget>
            <PipelineStepsWidget
              steps={entrySteps}
              theme={theme}
              label="Pipeline steps · promoted entry"
              onRefresh={() => reload()}
              refreshing={loading}
            />
          </div>
        )}


        <Widget label="Provenance" theme={theme}>
          <KV k="Run" v={`${cand.run_start_kind} · ${data?.run?.triggered_by ?? '—'} · ${compactDate(cand.run_started_at)}`} c={c} />
          <KV k="Strategy" v={cand.strategy_code ?? '—'} c={c} />
          <KV k="Gram" v={cand.gram ?? '—'} c={c} />
          <KV k="Query" v={cand.query_string ?? '—'} c={c} />
          {primaryGram && <KV k="Gram evidence" v={`tf ${primaryGram.term_freq} · df ${primaryGram.doc_freq} · score ${fmt(primaryGram.source_score)}`} c={c} />}
          <KV k="Corroboration" v={`${cand.corroboration_count} gram${cand.corroboration_count === 1 ? '' : 's'}`} c={c} />
          {otherGrams.length > 0 && (
            <div className="mt-1">
              <div className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>Also found by</div>
              <ul className={`mt-1 space-y-0.5 font-mono text-[11px] ${c.muted}`}>
                {otherGrams.map((g) => <li key={g.gram_id}>{g.strategy_code} · {g.query_string}</li>)}
              </ul>
            </div>
          )}
          {primaryGram?.provenance && (
            <div className="mt-2">
              <div className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>LLM gram provenance</div>
              <div className={`mt-1 text-[11px] leading-relaxed ${c.muted}`}>
                parents: {fmt(primaryGram.provenance.parents)} · confidence {fmt(primaryGram.provenance.confidence)} · {primaryGram.provenance.rationale ?? ''}
              </div>
            </div>
          )}
          <KV k="First seen (CT)" v={cand.first_seen ? compactDate(cand.first_seen) : '—'} c={c} />
          <KV k="Cert CN" v={cand.subject_common_name ?? '—'} c={c} />
          <KV k="Sourced" v={compactDate(cand.created_at)} c={c} />
          <KV k="Last seen" v={cand.last_seen_at ? `${compactDate(cand.last_seen_at)}${fmtAge(cand.last_seen_at) ? ` · ${fmtAge(cand.last_seen_at) === 'today' ? 'today' : `${fmtAge(cand.last_seen_at)} ago`}` : ''}` : '—'} c={c} />
        </Widget>

        <Widget label="Score" theme={theme}>
          <KV k="Quality" v={fmt(cand.quality_score)} c={c} />
          <KV k="term_freq score" v={fmt(cand.term_freq_score)} c={c} />
          <KV k="size score" v={`${fmt(cand.size_score)} (${cand.domain_size ?? '—'} chars, ${cand.label_count ?? '—'} labels)`} c={c} />
          <KV k="brand position" v={BRAND_POSITION_LABEL[cand.brand_position]} c={c} />
          <KV k="corroboration" v={String(cand.corroboration_count)} c={c} />
          <div className={`mt-2 text-[10px] leading-relaxed ${c.faint}`}>quality = 0.40·term_freq + 0.30·size + 0.15·corroboration + 0.15·lure (subdomain/both → 1, sld → 0.5)</div>
          <div className={`mt-3 border-t pt-2 ${c.line}`}>
            <KV k="Verdict" v={`${VERDICT_LABEL[v]} · ${fmt(cand.verdict_score)}`} c={c} />
            <KV k="Alive" v={fmt(cand.is_alive)} c={c} />
            <KV k="Lane" v={cand.verdict_vantage ? VANTAGE_LABEL[cand.verdict_vantage] : '—'} c={c} />
            <KV k="Basis" v={cand.verdict_basis ? <span title={BASIS_TITLE[cand.verdict_basis]}>{BASIS_LABEL[cand.verdict_basis]}</span> : (v === 'brand_lure' ? LEGACY_BASIS_LABEL : '—')} c={c} />
            <KV k="Last probe" v={cand.last_probe_verdict ? `${VERDICT_LABEL[cand.last_probe_verdict]}${cand.last_probe_vantage ? ` · ${VANTAGE_LABEL[cand.last_probe_vantage]}` : ''} · ${cand.last_probe_at ? compactDate(cand.last_probe_at) : '—'}` : '—'} c={c} />
            <KV k="Analysed" v={cand.analyzed_at ? compactDate(cand.analyzed_at) : '—'} c={c} />
            <KV k="Analysed by" v={firstContact ? `${firstContact.vantage ? VANTAGE_LABEL[firstContact.vantage] : '?'} · ${firstContact.originRun ? `${firstContact.originRun.job_name} · ${firstContact.originRun.trigger}${firstContact.originRun.requested_by ? ` · ${firstContact.originRun.requested_by}` : ''}` : 'no job (notebook / seed)'}` : '—'} c={c} />
            <KV k="Passes" v={String(data?.passes.length ?? 0)} c={c} />
          </div>
        </Widget>

        <Widget label="Registration (WHOIS)" theme={theme} headerRight={whoisRow?.raw_result?.raw_text ? <button onClick={() => setShowWhoisRaw((v) => !v)} className="font-mono text-[10px] uppercase tracking-widest text-signal hover:text-signal">{showWhoisRaw ? 'Hide raw' : 'Raw'}</button> : undefined}>
          {whoisRow?.raw_result ? (
            <>
              <KV k="Registered" v={whoisRow.raw_result.registered === false ? 'no record (unregistered)' : whoisRow.raw_result.registered ? 'yes' : 'unknown'} c={c} />
              <KV k="Created" v={whoisRow.raw_result.created_at ? `${compactDate(whoisRow.raw_result.created_at)}${whoisRow.raw_result.age_days != null ? ` · ${whoisRow.raw_result.age_days} days ago` : ''}` : '—'} c={c} />
              <KV k="Expires" v={whoisRow.raw_result.expires_at ? compactDate(whoisRow.raw_result.expires_at) : '—'} c={c} />
              <KV k="Updated" v={whoisRow.raw_result.updated_at ? compactDate(whoisRow.raw_result.updated_at) : '—'} c={c} />
              <KV k="Registrar" v={fmt(whoisRow.raw_result.registrar)} c={c} mono={false} />
              <KV k="Registrar URL" v={fmt(whoisRow.raw_result.registrar_url ?? cand.whois_registrar_url)} c={c} />
              <KV k="Abuse contact" v={abuseContact(whoisRow.raw_result.abuse_email ?? cand.whois_abuse_email, whoisRow.raw_result.abuse_phone ?? cand.whois_abuse_phone)} c={c} />
              <KV k="Registrant" v={`${fmt(whoisRow.raw_result.registrant_org)}${whoisRow.raw_result.registrant_country ? ` (${whoisRow.raw_result.registrant_country})` : ''}`} c={c} mono={false} />
              <KV k="Name servers" v={fmt(whoisRow.raw_result.name_servers)} c={c} />
              <KV k="Hosting" v={hostingLine(cand)} c={c} />
              <KV k="Status" v={fmt(whoisRow.raw_result.status)} c={c} />
              <KV k="Source" v={fmt(whoisRow.raw_result.source)} c={c} mono={false} />
              <KV k="Checked" v={compactDate(whoisRow.created_at)} c={c} />
              {showWhoisRaw && (
                <pre className={`mt-3 max-h-64 overflow-auto rounded-md border p-3 text-[10px] leading-relaxed whitespace-pre-wrap border-border bg-secondary text-muted-foreground`}>{whoisRow.raw_result.raw_text}</pre>
              )}
            </>
          ) : cand.whois_checked_at ? (
            <>
              <KV k="Registered" v={cand.whois_registered === false ? 'no record (unregistered)' : cand.whois_registered ? 'yes' : 'unknown'} c={c} />
              <KV k="Created" v={cand.whois_created_at ? `${compactDate(cand.whois_created_at)}${fmtAge(cand.whois_created_at) ? ` · ${fmtAge(cand.whois_created_at)} ago` : ''}` : '—'} c={c} />
              <KV k="Expires" v={cand.whois_expires_at ? compactDate(cand.whois_expires_at) : '—'} c={c} />
              <KV k="Registrar" v={fmt(cand.whois_registrar)} c={c} mono={false} />
              <KV k="Abuse contact" v={abuseContact(cand.whois_abuse_email, cand.whois_abuse_phone)} c={c} />
              <KV k="Hosting" v={hostingLine(cand)} c={c} />
              <KV k="Checked" v={compactDate(cand.whois_checked_at)} c={c} mono={false} />
            </>
          ) : (
            <span className={`font-mono text-xs uppercase tracking-[0.28em] ${c.faint}`}>Not looked up yet</span>
          )}
        </Widget>

        <Widget label="Review" theme={theme}>
          <KV k="Status" v={STATUS_LABEL[cand.status]} c={c} />
          {cand.filtered_reason && <KV k="Filtered" v={cand.filtered_reason} c={c} />}
          {cand.discarded_reason && <KV k="Discard reason" v={cand.discarded_reason} c={c} mono={false} />}
          {cand.review_note && <KV k="Note" v={cand.review_note} c={c} mono={false} />}
          {cand.promoted_at && <KV k="Promoted" v={`${compactDate(cand.promoted_at)} by ${cand.promoted_by ?? '—'}`} c={c} />}
          {cand.verdict_set_by && <KV k="Verdict set by hand" v={`${VERDICT_LABEL[cand.verdict as CandidateVerdict] ?? cand.verdict} · ${cand.verdict_set_at ? compactDate(cand.verdict_set_at) : '—'} by ${cand.verdict_set_by}`} c={c} />}
          {cand.user_entry_id && (
            <div className="mt-2 space-y-1">
              {[['user_entry_id', cand.user_entry_id], ['raw_entry_id', cand.raw_entry_id ?? '']].map(([label, value]) => (
                <div key={label} className="flex items-center justify-between gap-2">
                  <span className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>{label}</span>
                  <button onClick={() => copy(label, value)} className={`flex items-center gap-1 font-mono text-[10px] ${c.muted} hover:text-signal`} title="Copy">
                    <span className="max-w-[160px] truncate">{value}</span><Copy className="h-3 w-3" />{copied === label && <span className="text-success">copied</span>}
                  </button>
                </div>
              ))}
              <a data-action="open-entry" href={siblingDetailHref('unified', cand.user_entry_id)} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1 text-[10px] ${c.muted} hover:text-signal hover:underline`}>
                <ExternalLink className="h-3 w-3" /> Open the entry in Unified Entries (new tab)
              </a>
            </div>
          )}
          {cand.is_known && cand.domain_id && (
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>domain_id</span>
              <button onClick={() => copy('domain_id', cand.domain_id!)} className={`flex items-center gap-1 font-mono text-[10px] ${c.muted} hover:text-signal`}>
                <span className="max-w-[160px] truncate">{cand.domain_id}</span><Copy className="h-3 w-3" />{copied === 'domain_id' && <span className="text-success">copied</span>}
              </button>
            </div>
          )}
          {([['candidate_id', cand.candidate_id], ['run_id', cand.run_id], ['gram_id', cand.gram_id]] as Array<[string, string | null]>)
            .filter(([, v]) => v)
            .map(([label, value]) => (
              <div key={label} className="mt-2 flex items-center justify-between gap-2">
                <span className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>{label}</span>
                <button onClick={() => copy(label, value!)} className={`flex items-center gap-1 font-mono text-[10px] ${c.muted} hover:text-signal`} title={value!}>
                  <span className="max-w-[220px] truncate">{value}</span><Copy className="h-3 w-3" />{copied === label && <span className="text-success">copied</span>}
                </button>
              </div>
            ))}
        </Widget>

        <div className={detailColSpan(3)}>
          <Widget label={<>Analysis passes · {data?.passes.length ?? 0}</>} theme={theme} defaultCollapsed headerRight={<ShieldAlert className={`h-4 w-4 ${c.faint}`} />}>
            {data && data.passes.length > 0 ? (
              <div className="space-y-3">
                {data.passes.map((p, i) => <PassCard key={p.passId} pass={p} theme={theme} latest={i === 0} />)}
              </div>
            ) : (
              <span className={`font-mono text-xs uppercase tracking-[0.28em] ${c.faint}`}>Not analysed yet</span>
            )}
          </Widget>
        </div>

        <div className={detailColSpan(2)}>
          <Widget label={<>HTTP evidence{bestHttp ? <> · {passLabel(bestHttp.pass)}</> : null}</>} theme={theme}>
            {httpRow?.raw_result ? (
              <div className="grid gap-x-6 sm:grid-cols-2">
                <div>
                  <KV k="Status" v={fmt(httpRow.raw_result.status)} c={c} />
                  <KV k="Final URL" v={fmt(httpRow.raw_result.final_url)} c={c} />
                  <KV k="Redirects" v={fmt(httpRow.raw_result.redirect_chain)} c={c} />
                  <KV k="Offsite" v={fmt(httpRow.raw_result.offsite_redirect)} c={c} />
                  <KV k="Server" v={fmt(httpRow.raw_result.server)} c={c} />
                  <KV k="TLS verified" v={fmt(httpRow.raw_result.tls_verified)} c={c} />
                  <KV k="Proxy" v={fmt(httpRow.proxy_label)} c={c} />
                </div>
                <div>
                  <KV k="Title" v={fmt(httpRow.raw_result.title)} c={c} mono={false} />
                  <KV k="Forms / inputs" v={`${fmt(httpRow.raw_result.form_count)} / ${fmt(httpRow.raw_result.input_count)}`} c={c} />
                  <KV k="Password inputs" v={fmt(httpRow.raw_result.password_input_count)} c={c} />
                  <KV k="Card-like inputs" v={fmt(httpRow.raw_result.card_like_input_count)} c={c} />
                  <KV k="Brand hits" v={fmt(httpRow.raw_result.brand_hits)} c={c} />
                  <KV k="Script hosts" v={fmt(httpRow.raw_result.external_script_hosts)} c={c} />
                  <KV k="Text length" v={fmt(httpRow.raw_result.text_length)} c={c} />
                </div>
                {httpRow.raw_result.text_sample && (
                  <div className={`sm:col-span-2 mt-2 rounded border p-2 text-[11px] leading-relaxed ${c.line} ${c.soft} ${c.muted}`}>{httpRow.raw_result.text_sample}</div>
                )}
                {httpRow.raw_result.error && <div className="sm:col-span-2 mt-2 font-mono text-[11px] text-destructive">{httpRow.raw_result.error}</div>}
                {bestHttp && latestPass && bestHttp.pass.passId !== latestPass.passId && (
                  <div className={`sm:col-span-2 mt-2 font-mono text-[10px] ${c.faint}`} data-latest-probe title="A later pass saw less than this one (or looked from the lesser vantage); the best evidence stays the record">
                    latest probe · {passLabel(latestPass)} saw: {passVerdictLabel(latestPass)}
                  </div>
                )}
              </div>
            ) : (
              <span className={`font-mono text-xs uppercase tracking-[0.28em] ${c.faint}`}>No HTTP pass</span>
            )}
          </Widget>
        </div>

        {contentRow?.raw_result && (
          <div className={detailColSpan(2)}>
            <Widget label={<>Content analysis · e33{bestContent ? <> · {passLabel(bestContent.pass)}</> : null}</>} theme={theme}
              labelClassName={contentRow.raw_result.brand_impersonation ? 'text-destructive' : undefined}
              borderClassName={contentRow.raw_result.brand_impersonation ? 'border-destructive/40' : undefined}>
              {contentRow.raw_result.ok === false ? (
                <div className="font-mono text-[11px] text-destructive">e33 failed: {fmt(contentRow.raw_result.error)}</div>
              ) : (
                <div className="grid gap-x-6 sm:grid-cols-2">
                  <div>
                    <KV k="Brand impersonation" v={contentRow.raw_result.brand_impersonation ? 'YES' : (contentRow.raw_result.impersonates_other_brand ? 'other brand' : 'no')} c={c} />
                    <KV k="Credential harvesting" v={fmt(contentRow.raw_result.credential_harvesting)} c={c} />
                    <KV k="Fingerprinting cookies" v={fmt(contentRow.raw_result.fingerprinting)} c={c} />
                    <KV k="Page active" v={fmt(contentRow.raw_result.is_active)} c={c} />
                  </div>
                  <div>
                    <KV k="Tags" v={Array.isArray(contentRow.raw_result.tags) ? contentRow.raw_result.tags.join(', ') : fmt(contentRow.raw_result.tags)} c={c} mono={false} />
                  </div>
                  {contentRow.raw_result.impersonated_entity && (contentRow.raw_result.brand_impersonation || contentRow.raw_result.impersonates_other_brand) && (
                    <div className={`sm:col-span-2 mt-2 rounded border p-2 text-[11px] leading-relaxed ${c.text} ${contentRow.raw_result.brand_impersonation ? ('border-destructive/40 bg-danger-surface') : `${c.line} ${c.soft}`}`}>
                      {!contentRow.raw_result.brand_impersonation && <span className={`font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>impersonates a different brand · </span>}
                      {contentRow.raw_result.impersonated_entity}
                    </div>
                  )}
                  {Array.isArray(contentRow.raw_result.findings) && contentRow.raw_result.findings.length > 0 && (
                    <div className="sm:col-span-2 mt-2 space-y-1">
                      {contentRow.raw_result.findings.map((f: any, i: number) => (
                        <div key={i} className={`rounded border px-2 py-1 text-[11px] leading-relaxed ${c.line} ${c.soft}`}>
                          <span className={`font-mono ${c.faint}`}>{f.tag}</span>{f.description ? <span className={c.muted}> · {f.description}</span> : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Widget>
          </div>
        )}

        <Widget label={<>Screenshots · {browserRows.length}</>} theme={theme}>
          {browserRows.length > 0 ? (
            <CandidateScreenshots rows={browserRows} theme={theme} getToken={getScreenshotToken} />
          ) : (
            <div className={`rounded-md border px-4 py-8 text-center font-mono text-[11px] uppercase tracking-[0.24em] ${c.line} ${c.soft} ${c.faint}`}>
              {v === 'dead' || v === 'unreachable' ? 'Not rendered — host is down' : 'No browser pass yet'}
            </div>
          )}
        </Widget>
      </EntryDetailShell>
    </>
  )
}
